import { CssFromFunction } from '@upright/ui/common/generic/types/types';
import { alignToJustifyContent } from '../../utils/utils';

/**
 * These props and styles are common for both header and body cells in a table.
 */

export interface TableCellCommonStyleProps {
  align: 'left' | 'center' | 'right';
  $width?: number;
  css?: CssFromFunction;
  showVerticalBorders: boolean;
  metaCss?: CssFromFunction | string;
  isFirstColumn: boolean;
  /* Optional because Th is also used to hand-build tables outside TableV2 (see
  sdg-alignments/aggregate-alignments), where there is no column model to ask. Absent reads as
  false, i.e. the cell is not against the right edge of the frame. */
  isLastColumn?: boolean;
}

export function getTableCellCommonStyle(
  props: Pick<TableCellCommonStyleProps, 'align' | 'isFirstColumn' | 'isLastColumn'>,
) {
  const { align, isFirstColumn, isLastColumn } = props;
  /* A plain template string rather than styled-components' `css`: there is nothing here that needs
  interpolating as a component or a keyframe, and `css` returns an array whose elements are typed as
  objects — so the `.join('')` this used to end with was a lint error (`no-base-to-string`) in three
  places. Those errors are why PLT-6166 deferred the last-column padding below; PLT-6259 collects
  them. */
  return `
    display: flex;
    justify-content: ${alignToJustifyContent(align)};
    align-items: center;
    text-align: ${align};
    overflow: hidden;
    /* The outer columns are inset twice as far, so the content sits off the frame instead of against
    it — and by the same amount on both edges, which is why the last column is here too. The
    "&:first-of-type" / "&:last-of-type" css selectors don't work with nested headers, so which
    column this is has to be passed in. */
    padding-left: ${isFirstColumn ? '16px' : '8px'};
    padding-right: ${isLastColumn ? '16px' : '8px'};
  `;
}
